import {
  SUSTAINABILITY_MAX_STEPS,
  SUSTAINABILITY_MIN_STEPS,
  SUSTAINABILITY_SHORT_LABEL_MAX_LENGTH,
} from "@repo/shared/types";

// Local translations
export const T = {
  en_GB: {
    description:
      "Configure the sustainability criteria for this collection: an SFDR criterion (fixed Article 6/8/9 scores driven by Morningstar data), up to two extra scale criteria (bars), and any number of yes/no product preferences. Clients state the minimum they require in the flow and each portfolio is matched against them.",
    sfdrSection: "SFDR (Morningstar)",
    sfdrUse: "Use the SFDR criterion",
    sfdrLabel: "Label",
    sfdrQuestionLabel: "Question (shown to the client)",
    sfdrQuestionHint:
      "The client answers this in the flow to set their minimum required SFDR article.",
    sfdrQuestionPlaceholder: "e.g. What is the minimum SFDR category you want for your funds?",
    sfdrScoresLabel: "Article labels",
    sfdrScoresHint:
      "The three EU SFDR article scores are fixed (6, 8, 9) and resolved from each product's linked Morningstar instrument. Edit only their labels.",
    sfdrArticle: "Article",
    barsSection: "Bar criteria (scales)",
    barsDescription: "Up to two additional scale criteria, each with ordered steps.",
    productPreferencesSection: "Product preferences (yes/no)",
    productPreferencesDescription: "Boolean criteria the client can mark as wanted.",
    noPreferenceToggle: 'Offer a "No preference" option (required answer)',
    noPreferenceToggleHint:
      'The client must pick at least one preference or "No preference". Has no effect until at least one product preference exists.',
    displayLabel: "Display",
    displayHint: "How the preferences are shown to the client in the flow.",
    displayCheckbox: "Checkbox list",
    displayCards: "Cards",
    alignmentSection: "Portfolio alignment",
    alignmentDescription:
      "How the criteria above are matched against a portfolio. Pin a rule to apply it to every client of this collection, or leave the choice to the client.",
    alignmentIndividual: "Every fund must meet the preferences",
    alignmentIndividualHint: "Each instrument in the portfolio is assessed on its own.",
    alignmentPortfolio: "A share of the portfolio must meet the preferences",
    alignmentPortfolioHint:
      "A weighted share of the portfolio is assessed; the client states the required share.",
    alignmentClient: "The client decides",
    alignmentClientHint: "The client picks the rule while filling in the flow.",

    label: "Label",
    stepsLabel: "Steps (lowest first)",
    stepsHint: `Ordered from lowest to highest. Min ${SUSTAINABILITY_MIN_STEPS}, max ${SUSTAINABILITY_MAX_STEPS}.`,
    addStep: "Add step",
    moveUp: "Move up",
    moveDown: "Move down",
    stepPlaceholder: "e.g. Article 8",
    addBar: "Add bar",
    shortLabel: "Short label for tables (optional)",
    shortLabelPlaceholder: "e.g. EU Taxonomy",
    shortLabelHelp:
      "Used as the column header in the sustainability matching table, on screen and in the PDF. Left empty, the full question is used.",
    shortLabelTooLong: (bar: string, language: string) =>
      `The short label of "${bar}" is longer than ${SUSTAINABILITY_SHORT_LABEL_MAX_LENGTH} characters in ${language}`,
    addProductPreference: "Add product preference",
    remove: "Remove",
    save: "Save",
    saved: "Saved",
    savedDescription: "Sustainability configuration updated.",
    error: "Error",
    failedToSave: "Failed to save the sustainability configuration",

    labelPlaceholder: "e.g. Climate focus",
    labelRequired: "Every criterion needs a label",
    stepLabelRequired: "Every step needs a label",
    minStepsError: `Each scale needs at least ${SUSTAINABILITY_MIN_STEPS} steps`,
    productPreferenceIconLabel: "Icon",
    productPreferenceExclusionLabel: "Map to category",
    productPreferenceExclusionNone: "None (manual)",
    productPreferenceModeLabel: "Resolution",
    productPreferenceModeExcludes: "Excludes (stated policy)",
    productPreferenceModeNotInvolved: "Not involved",
    productPreferenceModeEither: "Either (excludes or not involved)",
    sfdrScoreLabelRequired: "Every SFDR article needs a label",
  },
  nb_NO: {
    description:
      "Konfigurer bærekraftskriteriene for denne samlingen: et SFDR-kriterium (faste Artikkel 6/8/9-verdier fra Morningstar-data), inntil to ekstra skalakriterier (søyler) og et valgfritt antall ja/nei-produktpreferanser. Kunden angir minstekravet i flyten, og hver portefølje matches mot dem.",
    sfdrSection: "SFDR (Morningstar)",
    sfdrUse: "Bruk SFDR-kriteriet",
    sfdrLabel: "Etikett",
    sfdrQuestionLabel: "Spørsmål (vist til kunden)",
    sfdrQuestionHint: "Kunden svarer på dette i flyten for å angi minste påkrevde SFDR-artikkel.",
    sfdrQuestionPlaceholder: "f.eks. Hvilken SFDR-kategori ønsker du som minimum for fondene dine?",
    sfdrScoresLabel: "Artikkeletiketter",
    sfdrScoresHint:
      "De tre EU SFDR-artikkelverdiene er faste (6, 8, 9) og hentes fra hvert produkts tilknyttede Morningstar-instrument. Rediger kun etikettene.",
    sfdrArticle: "Artikkel",
    barsSection: "Søylekriterier (skalaer)",
    barsDescription: "Inntil to ekstra skalakriterier, hver med ordnede trinn.",
    productPreferencesSection: "Produktpreferanser (ja/nei)",
    productPreferencesDescription: "Boolske kriterier kunden kan markere som ønsket.",
    noPreferenceToggle: "Tilby et «Ingen preferanse»-alternativ (obligatorisk svar)",
    noPreferenceToggleHint:
      "Kunden må velge minst én preferanse eller «Ingen preferanse». Har ingen effekt før minst én produktpreferanse finnes.",
    displayLabel: "Visning",
    displayHint: "Hvordan preferansene vises for kunden i flyten.",
    displayCheckbox: "Avkrysningsliste",
    displayCards: "Kort",
    alignmentSection: "Porteføljetilpasning",
    alignmentDescription:
      "Hvordan kriteriene ovenfor vurderes mot en portefølje. Fest en regel slik at den gjelder alle kunder i denne samlingen, eller la kunden velge.",
    alignmentIndividual: "Hvert fond må oppfylle preferansene",
    alignmentIndividualHint: "Hvert instrument i porteføljen vurderes for seg.",
    alignmentPortfolio: "En andel av porteføljen må oppfylle preferansene",
    alignmentPortfolioHint:
      "En vektet andel av porteføljen vurderes; kunden oppgir nødvendig andel.",
    alignmentClient: "Kunden velger",
    alignmentClientHint: "Kunden velger regelen mens flyten fylles ut.",

    label: "Etikett",
    stepsLabel: "Trinn (laveste først)",
    stepsHint: `Ordnet fra laveste til høyeste. Min ${SUSTAINABILITY_MIN_STEPS}, maks ${SUSTAINABILITY_MAX_STEPS}.`,
    addStep: "Legg til trinn",
    moveUp: "Flytt opp",
    moveDown: "Flytt ned",
    stepPlaceholder: "f.eks. Artikkel 8",
    addBar: "Legg til søyle",
    shortLabel: "Kort etikett for tabeller (valgfri)",
    shortLabelPlaceholder: "f.eks. EU-taksonomi",
    shortLabelHelp:
      "Brukes som kolonneoverskrift i tabellen for bærekraftsmatching, på skjerm og i PDF. Står den tom, brukes hele spørsmålet.",
    shortLabelTooLong: (bar: string, language: string) =>
      `Den korte etiketten for «${bar}» er lengre enn ${SUSTAINABILITY_SHORT_LABEL_MAX_LENGTH} tegn på ${language}`,
    addProductPreference: "Legg til produktpreferanse",
    remove: "Fjern",
    save: "Lagre",
    saved: "Lagret",
    savedDescription: "Bærekraftskonfigurasjonen er oppdatert.",
    error: "Feil",
    failedToSave: "Kunne ikke lagre bærekraftskonfigurasjonen",

    labelPlaceholder: "f.eks. Klimafokus",
    labelRequired: "Hvert kriterium trenger en etikett",
    stepLabelRequired: "Hvert trinn trenger en etikett",
    minStepsError: `Hver skala trenger minst ${SUSTAINABILITY_MIN_STEPS} trinn`,
    productPreferenceIconLabel: "Ikon",
    productPreferenceExclusionLabel: "Koble til kategori",
    productPreferenceExclusionNone: "Ingen (manuell)",
    productPreferenceModeLabel: "Utledning",
    productPreferenceModeExcludes: "Ekskluderer (uttalt policy)",
    productPreferenceModeNotInvolved: "Ikke involvert",
    productPreferenceModeEither: "Enten (ekskluderer eller ikke involvert)",
    sfdrScoreLabelRequired: "Hver SFDR-artikkel trenger en etikett",
  },
} as const;

/** Suffix appended to a category name to phrase a mapped product preference as a client preference. */
export const PRODUCT_PREFERENCE_LABEL_SUFFIX = {
  en_GB: { suffix: " exclusion / no involvement" },
  nb_NO: { suffix: " ekskludering / ingen involvering" },
} as const;
